/**
 * Assessment 2 of the saya yield engine — **set placement on a strip**, per
 * material.
 *
 * ── The assessment ──────────────────────────────────────────────────────────
 *
 * Each material is its own physical cutting job, so each gets its own strip of
 * a fixed width and its own gap preset. The size-set (quantities, mirrored
 * instances, direction rules already applied by the caller) is laid down with a
 * FIXED repeat count — no adaptive stop rule, because a reproducible number
 * beats a marginally better one that nobody can re-derive.
 *
 *     gross_area = widthMm × usedLengthMm
 *     η_set      = Σ A_net(placed) / gross_area
 *
 * `widthMm` is whatever the material's plane says it is: the REAL roll width
 * for roll goods (astar), the sheet width for sheet goods (fortluk), or the
 * configured "değerlendirme genişliği" for leather — which the design labels
 * an in-house convention and explicitly NOT a purchased-hide simulation.
 *
 * Consumption in the purchase unit is the caller's arithmetic; this module
 * stops at areas and the used length, because that is where the geometry ends
 * and unit conversion begins.
 *
 * ── The placement ───────────────────────────────────────────────────────────
 *
 * Bottom-left placement on the mm grid from `geometry.ts`. Instances are placed
 * in the order given, the whole list repeated `repeats` times; for each one
 * every rotation in `rotationsDeg` is tried and the lowest landing wins. Ties
 * break in a fixed order — lowest y, then lowest x, then the rotation whose
 * placed bbox TOP is lowest (the least skyline raise), then the FIRST rotation
 * in the list — so the layout is a pure function of its inputs. No randomness,
 * no annealing, no wall-clock budget.
 *
 * `stripPlaceBest` runs that placer over a small fixed set of feeding orders
 * and keeps the shortest strip — see its own comment. It is what the live card
 * and the zarf call; `stripPlace` stays exported as the single-order primitive
 * and as what `validateLayout` mirrors.
 *
 * The clearance comes from the same dilation trick as everywhere else: the
 * candidate is dilated by the gap and tested against the undilated occupancy of
 * what is already down. The gap is deliberately NOT applied against the strip
 * edges — a roll's selvedge margin is a separate material property, not a
 * cutting clearance.
 *
 * A piece that occupies no cell at all is REFUSED rather than laid — see
 * `DegenerateInstanceError`. An empty footprint clears everything and would
 * land on top of the layout while still counting its area.
 *
 * This is a greedy heuristic and it is meant to be: the design's stance is
 * "client computes, server verifies". `validateLayout` is that verification —
 * an independent re-check on a fresh grid, re-rasterising each placement from
 * its persisted geometry rather than trusting anything the placer built.
 */

import {
  DEFAULT_CELL_MM,
  type Part,
  type Point,
  type Raster,
  bbox,
  partNetArea,
  rasterDilate,
  rasterRuns,
  ringToRaster,
  rotate,
  translate,
} from './geometry'
import { YIELD_ALGO_VERSION } from './scaleArea'

/* ── Public contract ─────────────────────────────────────────────────────── */

export interface StripOptions {
  /** Strip width, mm — roll width, sheet width, or leather değerlendirme genişliği. */
  widthMm: number
  /** Cutter clearance between pieces, mm. Not applied against the strip edges. */
  gapMm: number
  /** Assessment grid resolution, mm. Defaults to `DEFAULT_CELL_MM` (1 mm). */
  cellMm?: number
  /** Rotations to try per instance, degrees CCW, in preference order. */
  rotationsDeg: number[]
  /** How many times the whole instance list is laid down. */
  repeats: number
}

export interface Placement {
  /** Index into the `instances` array this placement came from. */
  index: number
  /** Translation applied AFTER `rotDeg`, mm. */
  xMm: number
  /** Translation applied AFTER `rotDeg`, mm. */
  yMm: number
  /** Rotation applied to the instance's rings before translating, degrees CCW. */
  rotDeg: number
}

export interface StripResult {
  /** Length of strip consumed, mm — the top of the highest occupied cell. */
  usedLengthMm: number
  /** `widthMm × usedLengthMm`, mm². */
  grossAreaMm2: number
  /** `Σ A_net(placed) / grossAreaMm2`. */
  efficiency: number
  placements: Placement[]
  algoVersion: string
}

export interface LayoutValidation {
  ok: boolean
  /**
   * Developer-facing diagnostics, English by repo rule. These are the reasons a
   * layout was REJECTED at the trust boundary, not UI copy — a screen that
   * wants to tell a user something shows its own Turkish message.
   */
  problems: string[]
}

/** Raised when an instance cannot be laid on the strip at any allowed rotation. */
export class StripWidthError extends Error {
  readonly index: number
  constructor(index: number, message: string) {
    super(message)
    this.name = 'StripWidthError'
    this.index = index
  }
}

/**
 * Raised when an instance has a positive area but rasterises to NO cells at
 * every allowed rotation — a sliver thinner than the grid can see, e.g. a
 * 100 × 1 mm strap assessed on 2 mm cells, whose scanline never crosses a cell
 * centre.
 *
 * Such a piece would otherwise land anywhere at all: an empty footprint clashes
 * with nothing, so the placer drops every one of them at (0, 0), on top of each
 * other and of whatever is there, and the net area they still contribute makes
 * the reported efficiency exceed 1. Refusing is the honest answer — the grid
 * cannot assess this piece, and no layout it appears in means anything.
 *
 * It is a `StripWidthError` because it is the same event to every caller: this
 * instance cannot be laid at any allowed rotation, skip the width / report the
 * material. Only a call site that wants to SAY which of the two happened needs
 * to test for this class, and it must test for it first.
 */
export class DegenerateInstanceError extends StripWidthError {
  constructor(index: number, message: string) {
    super(index, message)
    this.name = 'DegenerateInstanceError'
  }
}

/* ── Placement ───────────────────────────────────────────────────────────── */

export function stripPlace(instances: Part[], opts: StripOptions): StripResult {
  const cellMm = opts.cellMm ?? DEFAULT_CELL_MM
  if (!(cellMm > 0)) throw new Error('stripPlace: cellMm must be > 0')
  if (!(opts.widthMm > 0)) throw new Error('stripPlace: widthMm must be > 0')
  if (!(opts.gapMm >= 0)) throw new Error('stripPlace: gapMm must be >= 0')
  if (!Number.isInteger(opts.repeats) || opts.repeats < 1) {
    throw new Error('stripPlace: repeats must be a positive integer')
  }
  if (opts.rotationsDeg.length === 0) {
    throw new Error('stripPlace: rotationsDeg must list at least one rotation')
  }

  // Both roundings go the same way on purpose — the assessment must never
  // flatter the layout. The gap rounds UP to a whole cell, and a strip width
  // that is not a whole number of cells loses the remainder for PLACEMENT while
  // the gross area below still charges the full width.
  const gapCells = Math.max(0, Math.ceil(opts.gapMm / cellMm))
  const widthCells = Math.floor(opts.widthMm / cellMm)
  if (widthCells < 1) throw new Error('stripPlace: widthMm is narrower than one cell')

  const grid = new Occupancy(widthCells)
  const shapes = new ShapeCache(instances, opts.rotationsDeg, cellMm, gapCells)
  const placements: Placement[] = []
  let netAreaMm2 = 0

  // Refuse a piece the grid cannot see BEFORE anything is laid: it would land
  // free of every constraint and quietly corrupt the whole assessment. The
  // shapes this reads are the ones the placement loop goes on to use, so the
  // pass costs nothing beyond the check itself.
  for (let index = 0; index < instances.length; index++) {
    let visible = false
    for (let slot = 0; slot < opts.rotationsDeg.length && !visible; slot++) {
      if (shapes.get(index, slot).occupiedCells > 0) visible = true
    }
    if (!visible) {
      throw new DegenerateInstanceError(
        index,
        `stripPlace: instance ${index} occupies no cell of the ${cellMm} mm grid at any of the given rotations`,
      )
    }
  }

  for (let pass = 0; pass < opts.repeats; pass++) {
    for (let index = 0; index < instances.length; index++) {
      const landing = land(grid, shapes, index, opts.rotationsDeg, gapCells, widthCells)
      if (landing === null) {
        throw new StripWidthError(
          index,
          `stripPlace: instance ${index} does not fit a ${opts.widthMm} mm strip at any of the given rotations`,
        )
      }
      const shape = shapes.get(index, landing.rotationSlot)
      grid.stamp(shape.piece, landing.gx, landing.gy)
      placements.push({
        index,
        xMm: (landing.gx - shape.piece.originCellX) * cellMm,
        yMm: (landing.gy - shape.piece.originCellY) * cellMm,
        rotDeg: opts.rotationsDeg[landing.rotationSlot] as number,
      })
      netAreaMm2 += partNetArea(instances[index] as Part)
    }
  }

  const usedLengthMm = grid.top * cellMm
  const grossAreaMm2 = opts.widthMm * usedLengthMm
  return {
    usedLengthMm,
    grossAreaMm2,
    efficiency: grossAreaMm2 > 0 ? netAreaMm2 / grossAreaMm2 : 0,
    placements,
    algoVersion: YIELD_ALGO_VERSION,
  }
}

/* ── Multi-order search ──────────────────────────────────────────────────── */

/**
 * The feeding orders `stripPlaceBest` may try, by name:
 *
 *   as-given     the caller's own order (the live card feeds biggest-net-first)
 *   diagonal     longest bbox diagonal first — long pieces early, before the
 *                strip narrows around them
 *   tallest      tallest bbox (at rotation 0) first — levels the skyline the
 *                way a shelf packer would
 *   interleave   sorted by net area descending, then taken from the front and
 *                the back alternately — big piece, small filler, big piece …
 *
 * All four are pure functions of the instance list; ties inside a sort break
 * on the original index, so every ordering is deterministic.
 */
export type StripOrdering = 'as-given' | 'diagonal' | 'tallest' | 'interleave'

/** The full variant set, in the priority order a result tie is broken in. */
export const STRIP_BEST_ORDERINGS: readonly StripOrdering[] = [
  'as-given',
  'diagonal',
  'tallest',
  'interleave',
]

/**
 * `stripPlace` over a small DETERMINISTIC set of feeding orders, keeping the
 * result that consumes the least strip. One greedy pass with one fixed order
 * leaves obvious holes on real sets; trying a handful of orders is the cheapest
 * honest improvement — no randomness, no annealing, just N placements instead
 * of one, each itself a pure function of its inputs. The smallest
 * `usedLengthMm` wins; a tie keeps the EARLIER variant, so the answer is as
 * reproducible as a single run.
 *
 * The placements' `index` contract is unchanged: every returned placement
 * indexes into the CALLER's `instances` array, whatever order the winning
 * variant fed the placer in, and the placements come back in `stripPlace`'s
 * own shape — the whole set once per repeat, in instance order.
 *
 * Callers with a work budget can pass a subset of `STRIP_BEST_ORDERINGS`; the
 * zarf's candidate-width search does — with its own objective, see
 * `envelope.ts`, which is why the search is split into the two exported pieces
 * below rather than living inside this function.
 */
export function stripPlaceBest(
  instances: Part[],
  opts: StripOptions,
  orderings: readonly StripOrdering[] = STRIP_BEST_ORDERINGS,
): StripResult {
  let best: StripResult | null = null
  for (const perm of stripOrderingVariants(instances, orderings)) {
    const result = stripPlaceVariant(instances, opts, perm)
    if (best === null || result.usedLengthMm < best.usedLengthMm) best = result
  }
  // `orderings` is non-empty and every variant either returned or threw.
  return best as StripResult
}

/**
 * The DISTINCT placement runs `orderings` collapses to for this instance list —
 * one permutation of caller indices each, in `orderings` order.
 *
 * Two orderings are the same run when they feed the same SHAPES in the same
 * sequence, not when they happen to list the same INDICES. The case that
 * matters is one piece at a high quantity: 'interleave' weaves the index list
 * front-to-back, so its permutation differs from the identity while the
 * geometry it hands the placer is identical cell for cell — deduping on
 * indices alone runs that placement a second time for an answer already known,
 * which on a live-sized set is the whole run time doubled.
 *
 * Instances are compared by REFERENCE. Callers that repeat a piece hand back
 * the same `Part` object (the live card's `instancesOf` does), and two
 * separately built objects are treated as distinct rather than deep-compared —
 * the cheap direction is also the safe one, since a missed match only costs a
 * redundant run while a false one would drop a real variant.
 *
 * The earliest ordering that produces a sequence keeps it, so a downstream tie
 * still resolves toward the earlier variant.
 */
export function stripOrderingVariants(
  instances: Part[],
  orderings: readonly StripOrdering[] = STRIP_BEST_ORDERINGS,
): number[][] {
  if (orderings.length === 0) {
    throw new Error('stripOrderingVariants: orderings must list at least one ordering')
  }

  const ids = new Map<Part, number>()
  const idOf = instances.map((instance) => {
    const seen = ids.get(instance)
    if (seen !== undefined) return seen
    const next = ids.size
    ids.set(instance, next)
    return next
  })

  const variants: number[][] = []
  const seen = new Set<string>()
  for (const ordering of orderings) {
    const perm = orderingPermutation(instances, ordering)
    const signature = perm.map((original) => idOf[original] as number).join(',')
    if (seen.has(signature)) continue
    seen.add(signature)
    variants.push(perm)
  }
  return variants
}

/**
 * One variant run: `stripPlace` fed in `perm`'s order, with everything mapped
 * back into the CALLER's frame — placements re-indexed and reshaped by
 * `remapToOriginal`, and a refusal re-raised with `index` naming the caller's
 * instance rather than the permuted one.
 *
 * Every multi-order search in this library goes through here, so they all
 * share one answer to "what does a caller see when a variant refuses".
 */
export function stripPlaceVariant(
  instances: Part[],
  opts: StripOptions,
  perm: readonly number[],
): StripResult {
  const permuted = perm.map((original) => instances[original] as Part)
  let result: StripResult
  try {
    result = stripPlace(permuted, opts)
  } catch (error: unknown) {
    // A refusal — too wide for the strip, or invisible to the grid — is a
    // per-instance fact, independent of the feeding order. Re-running the
    // caller's own order rethrows the same refusal with `index` in the
    // caller's frame instead of the permuted one.
    if (error instanceof StripWidthError && !isIdentity(perm)) stripPlace(instances, opts)
    throw error
  }
  return remapToOriginal(result, perm, instances.length)
}

function orderingPermutation(instances: Part[], ordering: StripOrdering): number[] {
  const indices = instances.map((_, index) => index)
  switch (ordering) {
    case 'as-given':
      return indices
    case 'diagonal': {
      const diagonals = instances.map((instance) => {
        const box = bbox(instance.outer)
        const width = box.maxX - box.minX
        const height = box.maxY - box.minY
        return width * width + height * height
      })
      return indices.sort(
        (a, b) => (diagonals[b] as number) - (diagonals[a] as number) || a - b,
      )
    }
    case 'tallest': {
      const heights = instances.map((instance) => {
        const box = bbox(instance.outer)
        return box.maxY - box.minY
      })
      return indices.sort((a, b) => (heights[b] as number) - (heights[a] as number) || a - b)
    }
    case 'interleave': {
      const areas = instances.map((instance) => partNetArea(instance))
      const sorted = [...indices].sort(
        (a, b) => (areas[b] as number) - (areas[a] as number) || a - b,
      )
      const out: number[] = []
      let front = 0
      let back = sorted.length - 1
      while (front <= back) {
        out.push(sorted[front] as number)
        if (front !== back) out.push(sorted[back] as number)
        front += 1
        back -= 1
      }
      return out
    }
  }
}

function isIdentity(perm: readonly number[]): boolean {
  for (let i = 0; i < perm.length; i++) if (perm[i] !== i) return false
  return true
}

/**
 * Map a permuted run's placements back into the caller's frame and restore
 * `stripPlace`'s own placement shape — each repeat pass in ascending instance
 * order — so a caller cannot tell WHICH variant won from anything but the
 * numbers. Reordering placements is free of meaning: a placement is a fact
 * about where a piece lies, not about when it was laid, and `validateLayout`
 * checks every pair regardless of order.
 */
function remapToOriginal(result: StripResult, perm: readonly number[], count: number): StripResult {
  if (isIdentity(perm)) return result
  const placements: Placement[] = []
  for (let start = 0; start < result.placements.length; start += count) {
    const pass = result.placements
      .slice(start, start + count)
      .map((placement) => ({ ...placement, index: perm[placement.index] as number }))
    pass.sort((a, b) => a.index - b.index)
    placements.push(...pass)
  }
  return { ...result, placements }
}

interface Landing {
  gx: number
  gy: number
  rotationSlot: number
  /** Row the skyline reaches once this landing is stamped — `gy + occupiedTop`. */
  skylineGy: number
}

/**
 * Lowest landing for one instance across every allowed rotation. Rotations are
 * compared on (y, x), then — because several rotations often land on the very
 * same cell of an empty stretch — on how far the landing raises the SKYLINE:
 * the rotation that raises it least leaves the most room for everything after
 * it. The earlier rotation keeps a full tie, which is what makes "first
 * rotation" a real rule rather than an accident of iteration order.
 *
 * The raise is measured from the shape's occupied rows (`occupiedTop`), not
 * from its raster frame — a frame whose top rows are empty would otherwise be
 * charged for length the strip never gives up, and the tie could go to a
 * rotation that consumes MORE than the one it beat.
 */
function land(
  grid: Occupancy,
  shapes: ShapeCache,
  index: number,
  rotationsDeg: number[],
  gapCells: number,
  widthCells: number,
): Landing | null {
  let best: Landing | null = null
  for (let slot = 0; slot < rotationsDeg.length; slot++) {
    const shape = shapes.get(index, slot)
    // An empty footprint would "fit" everywhere and win on (y, x) against every
    // real landing; `stripPlace` has already refused an instance that is empty
    // at EVERY rotation, so skipping here only drops the bad rotations of one
    // that can still be laid.
    if (shape.piece.width > widthCells || shape.occupiedCells === 0) continue
    const spot = bottomLeft(grid, shape, gapCells, widthCells)
    if (spot === null) continue
    const skylineGy = spot.gy + shape.occupiedTop
    if (
      best === null ||
      spot.gy < best.gy ||
      (spot.gy === best.gy &&
        (spot.gx < best.gx || (spot.gx === best.gx && skylineGy < best.skylineGy)))
    ) {
      best = { gx: spot.gx, gy: spot.gy, rotationSlot: slot, skylineGy }
    }
  }
  return best
}

/**
 * First free cell in bottom-left order: rows from the strip start upwards,
 * columns left to right inside each row. The scan is bounded above by
 * `grid.top + gapCells`, the first row at which the dilated footprint cannot
 * reach anything already placed — so a landing always exists once the piece fits
 * the width, and the loop always terminates.
 */
function bottomLeft(
  grid: Occupancy,
  shape: Shape,
  gapCells: number,
  widthCells: number,
): { gx: number; gy: number } | null {
  const maxGx = widthCells - shape.piece.width
  if (maxGx < 0) return null
  const maxGy = grid.top + gapCells
  for (let gy = 0; gy <= maxGy; gy++) {
    for (let gx = 0; gx <= maxGx; gx++) {
      if (fits(grid, shape, gx, gy, gapCells, widthCells)) return { gx, gy }
    }
  }
  return null
}

function fits(
  grid: Occupancy,
  shape: Shape,
  gx: number,
  gy: number,
  gapCells: number,
  widthCells: number,
): boolean {
  const rows = shape.clearanceRuns
  const colBase = gx - gapCells
  const rowBase = gy - gapCells
  for (let j = 0; j < rows.length; j++) {
    const run = rows[j] as number[]
    if (run.length === 0) continue
    const gridRow = rowBase + j
    if (gridRow < 0 || gridRow >= grid.top) continue
    for (let t = 0; t < run.length; t += 2) {
      let from = colBase + (run[t] as number)
      let to = colBase + (run[t + 1] as number)
      if (from < 0) from = 0
      if (to > widthCells) to = widthCells
      if (grid.anyOccupied(gridRow, from, to)) return false
    }
  }
  return true
}

/* ── Validation — the server-side trust boundary ─────────────────────────── */

/**
 * Re-check a layout from scratch: every placement is re-rasterised from its own
 * persisted geometry on a FRESH grid, then tested for strip bounds and for the
 * gap against everything checked before it. Nothing the placer built is reused,
 * so a tampered or stale client result cannot smuggle an overlap past this.
 *
 * `cellMm` is optional and defaults to the assessment default; pass the same
 * value the placement used. Because a placement offset is always a whole number
 * of cells, the placed raster is rebuilt on exactly the cells the placer used —
 * see `placedRaster` for why the translation is applied to the raster rather
 * than to the ring — and a clean layout validates clean.
 */
export function validateLayout(
  placements: Placement[],
  instances: Part[],
  widthMm: number,
  gapMm: number,
  cellMm: number = DEFAULT_CELL_MM,
): LayoutValidation {
  const problems: string[] = []
  if (!(cellMm > 0)) return { ok: false, problems: ['cellMm must be > 0'] }
  if (!(widthMm > 0)) return { ok: false, problems: ['widthMm must be > 0'] }
  if (!(gapMm >= 0)) return { ok: false, problems: ['gapMm must be >= 0'] }

  const gapCells = Math.max(0, Math.ceil(gapMm / cellMm))
  const widthCells = Math.floor(widthMm / cellMm)
  if (widthCells < 1) return { ok: false, problems: ['widthMm is narrower than one cell'] }

  const grid = new Occupancy(widthCells)
  // Tolerance for the bounds test only: placements land on cell multiples, so a
  // real violation is at least one cell wide and this never masks one.
  const tol = cellMm * 1e-6

  for (let p = 0; p < placements.length; p++) {
    const placement = placements[p] as Placement
    const instance = instances[placement.index]
    if (instance === undefined) {
      problems.push(`placement ${p}: instance index ${placement.index} does not exist`)
      continue
    }
    if (!Number.isFinite(placement.xMm) || !Number.isFinite(placement.yMm)) {
      problems.push(`placement ${p}: non-finite translation`)
      continue
    }

    const ring = translate(rotate(instance.outer, placement.rotDeg), placement.xMm, placement.yMm)
    const box = bbox(ring)
    if (box.minX < -tol || box.maxX > widthMm + tol) {
      problems.push(
        `placement ${p} (instance ${placement.index}): x range ${round(box.minX)}..${round(box.maxX)} mm leaves the ${widthMm} mm strip`,
      )
    }
    if (box.minY < -tol) {
      problems.push(
        `placement ${p} (instance ${placement.index}): y starts at ${round(box.minY)} mm, before the strip origin`,
      )
    }

    const piece = placedRaster(instance.outer, placement, cellMm)
    if (piece.originCellX < 0 || piece.originCellX + piece.width > widthCells) {
      problems.push(
        `placement ${p} (instance ${placement.index}): occupies cells outside the strip width`,
      )
    }
    if (piece.originCellY < 0) {
      problems.push(
        `placement ${p} (instance ${placement.index}): occupies cells below the strip origin`,
      )
    }

    const clearance = rasterDilate(piece, gapCells)
    const runs = rasterRuns(clearance)
    let clash = false
    for (let j = 0; j < runs.length && !clash; j++) {
      const run = runs[j] as number[]
      const gridRow = clearance.originCellY + j
      if (gridRow < 0 || gridRow >= grid.top) continue
      for (let t = 0; t < run.length; t += 2) {
        let from = clearance.originCellX + (run[t] as number)
        let to = clearance.originCellX + (run[t + 1] as number)
        if (from < 0) from = 0
        if (to > widthCells) to = widthCells
        if (grid.anyOccupied(gridRow, from, to)) {
          clash = true
          break
        }
      }
    }
    if (clash) {
      problems.push(
        `placement ${p} (instance ${placement.index}): comes within ${gapMm} mm of an earlier placement`,
      )
    }

    grid.stamp(piece, piece.originCellX, piece.originCellY)
  }

  return { ok: problems.length === 0, problems }
}

function round(value: number): number {
  return Math.round(value * 1000) / 1000
}

/**
 * The world-frame raster of one placement.
 *
 * When the translation is a whole number of cells — which every placement the
 * placer emits is, by construction — the ring is rasterised UNtranslated, in
 * exactly the frame the placer's `ShapeCache` rasterised it in, and the offset
 * is applied to the raster's origin instead. Rasterising the translated ring
 * is NOT guaranteed to land on the same cells: a rotated edge that runs
 * exactly through cell centres (a 45° hypotenuse on the assessment grid does)
 * flips boundary cells on the float noise the translation adds, and one cell
 * gained toward a neighbour is a phantom clearance clash at this trust
 * boundary — a layout the placer built then fails its own verification.
 *
 * A translation that is NOT on the cell lattice (only a foreign or tampered
 * layout carries one) falls back to rasterising the translated ring, which is
 * as faithful as such a layout can be assessed.
 */
function placedRaster(outer: readonly Point[], placement: Placement, cellMm: number): Raster {
  const dx = placement.xMm / cellMm
  const dy = placement.yMm / cellMm
  const cx = Math.round(dx)
  const cy = Math.round(dy)
  if (Math.abs(dx - cx) < 1e-9 && Math.abs(dy - cy) < 1e-9) {
    const base = ringToRaster(rotate(outer, placement.rotDeg), cellMm)
    return { ...base, originCellX: base.originCellX + cx, originCellY: base.originCellY + cy }
  }
  return ringToRaster(
    translate(rotate(outer, placement.rotDeg), placement.xMm, placement.yMm),
    cellMm,
  )
}

/* ── Internals ───────────────────────────────────────────────────────────── */

interface Shape {
  /** The piece itself — what gets stamped, and what must stay inside the strip. */
  piece: Raster
  /** Runs of the piece dilated by the gap — what decides whether it may land. */
  clearanceRuns: number[][]
  /** Cells the piece actually sets. Zero means the grid cannot see this shape. */
  occupiedCells: number
  /**
   * Rows from the frame's bottom up to and including the HIGHEST occupied one —
   * how far this shape raises the skyline once it is stamped.
   *
   * This is NOT `piece.height`. The raster frame is the ring's bounding box in
   * cells, and its top rows come out empty whenever the outline passes above
   * every cell centre it crosses there — a rotated triangle's apex does exactly
   * that. The skyline only ever advances through occupied rows, so a tie-break
   * that reads the frame height compares a number the strip never pays.
   *
   * Zero when the shape sets no cell at all.
   */
  occupiedTop: number
}

/**
 * Rotating and rasterising is by far the most expensive thing per placement and
 * the same instance comes round on every repeat, so each (instance, rotation)
 * pair is built once.
 */
class ShapeCache {
  private readonly instances: Part[]
  private readonly rotationsDeg: number[]
  private readonly cellMm: number
  private readonly gapCells: number
  private readonly cache: (Shape | undefined)[]

  constructor(instances: Part[], rotationsDeg: number[], cellMm: number, gapCells: number) {
    this.instances = instances
    this.rotationsDeg = rotationsDeg
    this.cellMm = cellMm
    this.gapCells = gapCells
    this.cache = new Array(instances.length * rotationsDeg.length)
  }

  get(index: number, slot: number): Shape {
    const at = index * this.rotationsDeg.length + slot
    const hit = this.cache[at]
    if (hit !== undefined) return hit
    const instance = this.instances[index] as Part
    // Holes stay occupied: nothing nests inside another part's cut-out, so the
    // footprint is the outer ring alone.
    const ring: readonly Point[] = rotate(instance.outer, this.rotationsDeg[slot] as number)
    const piece = ringToRaster(ring, this.cellMm)
    let occupiedCells = 0
    let occupiedTop = 0
    for (let j = 0; j < piece.height; j++) {
      const base = j * piece.width
      let inRow = 0
      for (let i = 0; i < piece.width; i++) {
        if (piece.data[base + i] !== 0) inRow++
      }
      if (inRow > 0) {
        occupiedCells += inRow
        occupiedTop = j + 1
      }
    }
    const shape: Shape = {
      piece,
      clearanceRuns: rasterRuns(rasterDilate(piece, this.gapCells)),
      occupiedCells,
      occupiedTop,
    }
    this.cache[at] = shape
    return shape
  }
}

const WORD_BITS = 32

/** Bits `from`..31 of a word. */
function maskFrom(bit: number): number {
  return -1 << bit
}

/** Bits 0..`bit` of a word. */
function maskUpto(bit: number): number {
  return bit >= WORD_BITS - 1 ? -1 : (1 << (bit + 1)) - 1
}

/**
 * The strip's occupancy, one bit per cell, growing upwards as pieces land.
 *
 * A bitset rather than a byte grid because the inner loop asks "is anything
 * occupied between these two columns of this row" tens of thousands of times
 * per placement, and that question costs a couple of word reads here instead of
 * a scan over every cell.
 */
class Occupancy {
  readonly widthCells: number
  /** Highest occupied row + 1 — the used length in cells. */
  top = 0
  private readonly words: number
  private bits: Uint32Array
  private rows: number

  constructor(widthCells: number) {
    this.widthCells = widthCells
    this.words = Math.ceil(widthCells / WORD_BITS)
    this.rows = 64
    this.bits = new Uint32Array(this.rows * this.words)
  }

  anyOccupied(row: number, from: number, to: number): boolean {
    if (row < 0 || row >= this.top || from >= to) return false
    const base = row * this.words
    const firstWord = from >>> 5
    const lastWord = (to - 1) >>> 5
    const head = maskFrom(from & 31)
    const tail = maskUpto((to - 1) & 31)
    if (firstWord === lastWord) {
      return ((this.bits[base + firstWord] as number) & head & tail) !== 0
    }
    if (((this.bits[base + firstWord] as number) & head) !== 0) return true
    for (let w = firstWord + 1; w < lastWord; w++) {
      if ((this.bits[base + w] as number) !== 0) return true
    }
    return ((this.bits[base + lastWord] as number) & tail) !== 0
  }

  stamp(piece: Raster, gx: number, gy: number): void {
    const runs = rasterRuns(piece)
    this.ensureRows(gy + piece.height)
    for (let j = 0; j < runs.length; j++) {
      const run = runs[j] as number[]
      if (run.length === 0) continue
      const row = gy + j
      const base = row * this.words
      for (let t = 0; t < run.length; t += 2) {
        let from = gx + (run[t] as number)
        let to = gx + (run[t + 1] as number)
        if (from < 0) from = 0
        if (to > this.widthCells) to = this.widthCells
        for (let i = from; i < to; i++) {
          const at = base + (i >>> 5)
          this.bits[at] = (this.bits[at] as number) | (1 << (i & 31))
        }
      }
      if (row + 1 > this.top) this.top = row + 1
    }
  }

  private ensureRows(rows: number): void {
    if (rows <= this.rows) return
    let next = this.rows
    while (next < rows) next *= 2
    const grown = new Uint32Array(next * this.words)
    grown.set(this.bits)
    this.bits = grown
    this.rows = next
  }
}
